import { useMemo, useState, useEffect } from 'react';

import {
  CopyIcon,
  GridIcon,
  HeartStraightIcon,
  ListIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
  ClockOutlineIcon
} from '@c/icons';
import { GavelIcon } from '@c/products/AuctionBanner';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import ButtonToggle from '@ui/ButtonToggle';
import { Divider } from '@ui/Divider';
import SafeImage from '@ui/SafeImage';
import Select from '@ui/Select';
import {
  deleteListing,
  getSellerListingsPaginated,
  moveToDraft,
} from '@util/firestore/products';
import useCountdown from '@util/hooks/useCountdown';
import { formatCurrency } from '@util/index';
import { Condition } from '@util/maps/conditions';
import { ButtonToggleType } from '@util/types/buttontoggle';
import { ProductDocument } from '@util/types/firestore/products';
import { getProductUrl } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import EmptyState from '../EmptyState';
import QueryErrorState, {
  AuthStalledState,
  SignedOutState,
} from '../QueryErrorState';
import { useDashboardAuthState } from '@util/hooks/useDashboardAuthState';
import { reportDashboardError } from '@util/reportDashboardError';
import { daysUntilExpiration } from '@util/index';
import RenewListingModal from '@c/modals/RenewListingModal';
import { ConditionDocument } from '@util/types/firestore/conditions';
import { getConditions } from '@util/firestore/conditions';
import { publishToEbay } from '@util/gearsync/api/useEbayPublish';
import { withdrawFromEbay } from '@util/gearsync/api/useEbayWithdraw';
import { canUseProvider } from '@util/permissions/integrations';
import CheckmarkIconButton from '@c/icons/buttons/CheckmarkButton';

/**
 * "Sold" = stock-exhausted listing that isn't still a draft. Same logic the
 * SoldOverlay uses, so the badge and the bulk-action rules agree.
 */
function isSoldListing(p: ProductDocument): boolean {
  return p.out_of_stock === true && !p.is_draft;
}

function isDraftListing(p: ProductDocument): boolean {
  return !!p.is_draft;
}

/** Selectable for the Delete action — sold listings cannot be deleted. */
function canDelete(p: ProductDocument): boolean {
  return !isSoldListing(p);
}

/** Selectable for Send-to-eBay — needs to be live (not sold) and not a draft. */
function canSendToEbay(p: ProductDocument): boolean {
  return !isSoldListing(p) && !isDraftListing(p);
}

/** True if the listing is currently on eBay (queued or live), per the cached state. */
function isOnEbay(p: ProductDocument): boolean {
  return p.ebay_status === 'queued' || p.ebay_status === 'live';
}

/**
 * Per-listing eBay action button: shows "Remove from eBay" when the listing
 * is queued/live there, otherwise "Send to eBay". Hidden entirely for
 * ineligible listings (drafts, sold, Lightspeed-origin, no eBay perm).
 */
function EbayActionButton({ product }: { product: ProductDocument }) {
  const { userDoc } = useAuth();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const enabled = canUseProvider(
    userDoc?.permissions?.integrations,
    'ebay'
  );
  if (!enabled) return null;
  if (isSoldListing(product) || isDraftListing(product)) return null;
  if (product.integrations?.source === 'lightspeed-retail') return null;

  const onEbay = isOnEbay(product);

  async function run() {
    if (busy) return;
    setBusy(true);
    try {
      if (onEbay) {
        if (
          !window.confirm(
            'Remove this listing from eBay? It will stay live on GearFocus.'
          )
        )
          return;
        const results = await withdrawFromEbay([product.id]);
        const r = results.find((x) => x.id === product.id);
        if (r && r.status === 'error') {
          alert(`Couldn’t remove from eBay: ${r.error ?? 'unknown error'}`);
        }
      } else {
        const results = await publishToEbay([product.id]);
        const r = results.find((x) => x.id === product.id);
        if (r && r.status === 'error') {
          alert(`Couldn’t send to eBay: ${r.error ?? 'unknown error'}`);
        }
      }
      await queryClient.invalidateQueries(['listings']);
    } catch (e) {
      alert(
        e instanceof Error ? e.message : 'eBay action failed. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        type="text"
        text={
          busy
            ? onEbay
              ? 'Removing…'
              : 'Sending…'
            : onEbay
              ? 'Remove from eBay'
              : 'Send to eBay'
        }
        width="extraSmall"
        onClick={run}
        disabled={busy}
        noXPadding
      />
      <VerticalLine />
    </>
  );
}

const VerticalLine = () => (
  <div className="h-[2.4rem] w-[0.1rem] bg-brand-light-gray" />
);

const ExpirationCard = ({ product }: ListingCardProps) => {
  const [renewModal, setRenewModal] = useState<boolean>(false);
  const days = daysUntilExpiration(product.date_updated)
  
  return (
    <>
      {days !== null && days < 8 ? (
        <>
          <Button
            type="text"
            text={days > 0 ? `Expires in ${days} day${days < 2 ? '' : 's'}` : `Renew Listing`}
            leadingIcon={<ClockOutlineIcon />}
            width="extraSmall"
            onClick={() => { setRenewModal(true) }}
            noXPadding
          />
          <VerticalLine />
        </>
      ) : (
        <></>
      )}
      <RenewListingModal product={product} isOpen={renewModal} dismiss={() => { setRenewModal(false) }} />
    </>
  )
}

export const SoldOverlay = ({ variant }: { variant: 'auction' | 'sold' | 'expired' }) => (
  <div className="pointer-events-none absolute top-0 flex h-full w-full items-center justify-center">
    <div className=" flex h-[5rem] w-full items-center justify-center bg-brand-secondary font-semibold text-white opacity-80">
      {variant === 'auction' ? 'Auction Ended' : variant === 'sold' ? 'Item Sold' : variant === 'expired' ? 'Listing Expired' : ''}
    </div>
  </div>
);

export const DraftOverlay = ({ variant }: { variant: 'auction' | 'draft' }) => (
  <div className="absolute left-0 top-0 h-full w-full rounded-[10px] bg-brand-lightest-black/50">
    <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center">
      <div
        className={`w-full ${
          variant === 'auction' ? 'bg-orange-500' : 'bg-yellow-500'
        } px-[1.6rem] py-[1.4rem] text-center font-semibold text-brand-white opacity-50`}
      >
        {variant === 'auction' ? 'Auction Starting Soon' : 'Draft'}
      </div>
    </div>
  </div>
);

const EditDuplicateDeleteButtonsWithText = ({
  product,
}: {
  product: ProductDocument;
}) => {
  const queryClient = useQueryClient();
  return (
    <>
      {!product.integrations && !product.out_of_stock && !product.is_draft && (
        <ExpirationCard product={product} />
      )}

      {/* Move to Draft - only for live, non-integration listings */}
      {!product.integrations && !product.out_of_stock && !product.is_draft && !product.is_expired && (
        <>
          <Button
            type="text"
            text="Move to Draft"
            width="extraSmall"
            onClick={async () => {
              if (product.is_auction && product.end_time && product.end_time > Date.now()) {
                // Check for active bids on live auctions before allowing draft
                try {
                  const { getDocs, collection, query, where } = await import('firebase/firestore');
                  const { db } = await import('@util/firebase');
                  const bidsQuery = query(
                    collection(db, 'bids'),
                    where('product_id', '==', product.id)
                  );
                  const bidsSnap = await getDocs(bidsQuery);
                  if (bidsSnap.size > 0) {
                    alert('This auction has active bids and cannot be moved to draft.');
                    return;
                  }
                } catch {
                  alert('Unable to verify auction status. Please try again.');
                  return;
                }
              }
              if (
                window.confirm(
                  'This will hide your listing from the marketplace. Pending offers will be declined and buyers with this item in their cart will have it removed. Continue?'
                )
              ) {
                await moveToDraft(product.id);
                queryClient.invalidateQueries(['listings']);
              }
            }}
            noXPadding
          />
          <VerticalLine />
        </>
      )}

      {(product.is_draft || !product.out_of_stock || product.is_auction) && !product.is_expired && (
        <>
          <Button
            type="text"
            text={"Edit Item"}
            leadingIcon={<PencilSimpleIcon />}
            width="extraSmall"
            href={`/edit-listing/${product.id}`}
            noXPadding
          />
          <VerticalLine />{' '}
          <EbayActionButton product={product} />
        </>
      )}

      {(product.is_draft || !product.out_of_stock || product.is_auction) && (
        <>
          <div className="text-brand-red">
            <Button
              type="text"
              text="Delete"
              leadingIcon={<TrashIcon />}
              width="extraSmall"
              onClick={async () => {
                if (
                  window.confirm('Are you sure you want to delete this item?')
                ) {
                  try {
                    const result = await deleteListing(
                      product.id,
                      product.is_draft
                    );
                    if (result.archived) {
                      alert(
                        'This listing has sales history, so it was archived instead of deleted.'
                      );
                    }
                    queryClient.invalidateQueries(['listings']);
                  } catch (e) {
                    alert(
                      e instanceof Error
                        ? e.message
                        : 'Delete failed. Please try again.'
                    );
                  }
                }
              }}
              noXPadding
            />
          </div>
        </>
      )}
    </>
  );
};
const EditDuplicateDeleteButtons = ({
  product,
}: {
  product: ProductDocument;
}) => {
  const queryClient = useQueryClient();
  return (
  <>
    {!product.integrations && !product.out_of_stock && !product.is_draft && !product.is_expired && (
      <>
        <Button
          type="text"
          text="Draft"
          width="extraSmall"
          onClick={async () => {
            if (product.is_auction) {
              const { getDocs, collection, query, where } = await import('firebase/firestore');
              const { db } = await import('@util/firebase');
              const bidsQuery = query(
                collection(db, 'bids'),
                where('product_id', '==', product.id)
              );
              const bidsSnap = await getDocs(bidsQuery);
              if (bidsSnap.size > 0) {
                alert('This auction has active bids and cannot be moved to draft.');
                return;
              }
            }
            if (
              window.confirm(
                'Move to draft? This will hide your listing from the marketplace.'
              )
            ) {
              await moveToDraft(product.id);
              queryClient.invalidateQueries(['listings']);
            }
          }}
          noXPadding
        />
        <VerticalLine />
      </>
    )}

    {(product.is_draft || !product.out_of_stock || product.is_auction) && (
      <>
        <Button
          type="text"
          leadingIcon={<PencilSimpleIcon />}
          width="extraSmall"
          href={`/edit-listing/${product.id}`}
          noXPadding
        />
        <VerticalLine />
      </>
    )}

    <Link
      href={{
        pathname: '/sell/create-listing',
        query: { id: product.id },
      }}
      // hide the query string cause its ugly
      as="/sell/create-listing"
    >
      <div className="flex items-center gap-[0.4rem]">
        <CopyIcon />
      </div>
    </Link>

    {(product.is_draft || !product.out_of_stock || product.is_auction) && (
      <>
        <VerticalLine />
        <div className="text-brand-red">
          <Button
            type="text"
            leadingIcon={<TrashIcon />}
            onClick={async () => {
              if (
                window.confirm('Are you sure you want to delete this item?')
              ) {
                try {
                  const result = await deleteListing(
                    product.id,
                    product.is_draft
                  );
                  if (result.archived) {
                    alert(
                      'This listing has sales history, so it was archived instead of deleted.'
                    );
                  }
                  queryClient.invalidateQueries(['listings']);
                } catch (e) {
                  alert(
                    e instanceof Error
                      ? e.message
                      : 'Delete failed. Please try again.'
                  );
                }
              }
            }}
            width="extraSmall"
            noXPadding
          />
        </div>
      </>
    )}
  </>
  );
};

interface ListingCardProps {
  product: ProductDocument;
  /** When provided, renders a selection checkbox on the card. */
  selection?: SelectionApi;
}

/**
 * Selection checkbox shown over the card image, using the brand
 * CheckmarkIconButton. Only rendered for selectable (non-sold) listings —
 * callers gate on `!isSoldListing(product)`. The card image gets a brand
 * ring when selected — see `selectionRing`.
 */
function SelectionCheckbox({
  product,
  selection,
}: {
  product: ProductDocument;
  selection: SelectionApi;
}) {
  return (
    <div
      className="absolute left-[0.8rem] top-[0.8rem] z-30 flex h-[2.8rem] w-[2.8rem] items-center justify-center rounded-[0.8rem] bg-brand-white/95 shadow-sm ring-1 ring-brand-lighter-gray backdrop-blur-sm"
      // Stop the click from following the card's Link.
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <CheckmarkIconButton
        checked={selection.isSelected(product.id)}
        onClick={() => selection.toggle(product.id)}
        label=""
      />
    </div>
  );
}

/** Ring classes applied to a card's image when the listing is selected. */
function selectionRing(selected: boolean | undefined): string {
  return selected
    ? 'ring-2 ring-brand-secondary ring-offset-2 ring-offset-brand-white'
    : '';
}

const ListingCardGrid = ({ product, selection }: ListingCardProps) => {
  const isStarted = product.start_time! < Date.now();
  const timeRemaining = useCountdown(
    product.start_time!,
    product.end_time!,
    isStarted ? 'end' : 'start'
  );

  return (
    <div className=" isolate flex w-[20rem] flex-col gap-[0.4rem]">
      <Link
        href={getProductUrl(product.slug)}
        prefetch={false}
        className={`relative h-[20rem] w-[20rem] rounded-[10px] transition-shadow ${selectionRing(
          selection?.isSelected(product.id)
        )}`}
      >
        {selection && !isSoldListing(product) && (
          <SelectionCheckbox product={product} selection={selection} />
        )}
        <SafeImage
          className="h-full w-full rounded-[10px]"
          alt={product.title}
          key={product.images?.[0]?.thumb}
          src={product.images?.[0]?.thumb}
        />

        {product.out_of_stock && !product.is_draft && !product.is_auction && (
          <SoldOverlay variant="sold" />
        )}

        {!product.out_of_stock && !product.is_draft && product.is_expired && (
          <SoldOverlay variant="expired" />
        )}

        {product.is_draft && <DraftOverlay variant="draft" />}
        {product.is_auction &&
          product.end_time! > Date.now() &&
          !product.is_draft && (
          <div className="absolute bottom-0 left-0 z-20 flex h-[4rem] w-full items-center justify-center gap-[0.8rem] rounded-b-[10px] bg-gradient-orange  text-white">
              <GavelIcon />
              <span className="w-[15rem] whitespace-nowrap  text-[1.3rem] font-medium sm:text-[1.5rem]">
                {isStarted ? 'Ends in ' : 'Starts in '}
                {timeRemaining.days > 0 && `${timeRemaining.days}d `}
                {timeRemaining.hours}h {timeRemaining.minutes}m{' '}
                {timeRemaining.seconds}s
              </span>
            </div>
          )}
        {product.is_auction &&
          product.end_time! < Date.now() &&
          !product.is_draft && (
          <div className="absolute bottom-0 left-0 z-20 flex h-[4rem] w-full items-center justify-center gap-[0.8rem] rounded-b-[10px] bg-green-500 text-white">
              <GavelIcon />
              <span className="w-[15rem] whitespace-nowrap text-[1.8rem] font-medium">
                Sold for {formatCurrency(product.price ?? 0)}!
              </span>
            </div>
          )}
      </Link>
      <Link href={getProductUrl(product.slug)} prefetch={false} className="flex w-full flex-col">
        <span className="line-clamp-2 text-[1.3rem] leading-[2rem] text-brand-light-black">
          {product.title}
        </span>
        <div className="isolate flex items-center gap-[0.8rem]">
          {product.on_sale ? (
            <div className="flex items-center gap-[0.8rem]">
              <span className=" font-semibold  text-brand-lightest-black">
                {formatCurrency(product.price)}
              </span>
              <span className=" text-[1.3rem] font-semibold leading-[1.5rem] text-brand-gray line-through">
                {formatCurrency(product.og_price)}
              </span>
            </div>
          ) : (
            <span className="font-semibold">
              {formatCurrency(product.price)}
            </span>
          )}
        </div>
      </Link>
      <div className="flex w-full justify-between gap-[0.4rem]">
        <span className="grow text-[1.3rem] text-brand-lightest-black">
          Stock: {product.stock}
        </span>
        <div className="flex gap-[0.8rem]">
          {/* <div className="flex gap-[0.4rem] items-center">
          <EyeOutlineIcon />
          <span className="text-[1.3rem] text-brand-lightest-black">
            {product.favorite_count}
          </span>
        </div> */}
          <div className="flex items-center gap-[0.4rem]">
            <HeartStraightIcon />
            <span className="text-[1.3rem] text-brand-lightest-black">
              {product.favorite_count}
            </span>
          </div>
        </div>
      </div>
      <div className="flex h-[4rem] w-full items-center justify-between gap-[1.6rem] px-[1.6rem] py-[0.8rem]">
        <EditDuplicateDeleteButtons product={product} />
      </div>
    </div>
  );
};

const ListingCardList = ({ product, selection }: ListingCardProps) => {
  const isStarted = product.start_time! < Date.now();
  const timeRemaining = useCountdown(
    product.start_time!,
    product.end_time!,
    isStarted ? 'end' : 'start'
  );
  return (
    <div className="isolate flex w-full flex-col gap-[1.2rem] lg:flex-row">
      <Link
        href={getProductUrl(product.slug)}
        prefetch={false}
        className={`relative min-h-[34rem] min-w-[34rem] rounded-[10px] transition-shadow lg:min-h-[22rem] lg:min-w-[22rem] ${selectionRing(
          selection?.isSelected(product.id)
        )}`}
      >
        {selection && !isSoldListing(product) && (
          <SelectionCheckbox product={product} selection={selection} />
        )}
        <SafeImage
          className="h-[34rem] w-[34rem] rounded-[10px] object-contain lg:h-[22rem] lg:w-[22rem]"
          width={220}
          height={220}
          alt={product.title}
          key={product.images?.[0]?.thumb}
          src={product.images?.[0]?.thumb}
        />

        {product.out_of_stock && !product.is_draft && !product.is_auction && (
          <SoldOverlay variant={'sold'} />
        )}

        {!product.out_of_stock && !product.is_draft && product.is_expired && (
          <SoldOverlay variant="expired" />
        )}

        {product.is_draft && <DraftOverlay variant="draft" />}
        {product.is_auction &&
          product.end_time! > Date.now() &&
          !product.is_draft && (
          <div className="absolute bottom-0 left-0 z-20 flex h-[5rem] w-full items-center justify-center gap-[0.8rem] rounded-b-[10px] bg-gradient-orange text-white">
              <GavelIcon />
              <span className="w-[15rem] whitespace-nowrap text-[1.5rem] font-medium">
                {isStarted ? 'Ends in ' : 'Starts in '}
                {timeRemaining.days > 0 && `${timeRemaining.days}d `}
                {timeRemaining.hours}h {timeRemaining.minutes}m{' '}
                {timeRemaining.seconds}s
              </span>
            </div>
          )}
        {product.is_auction &&
          product.end_time! < Date.now() &&
          !product.is_draft && (
          <div className="absolute bottom-0 left-0 z-20 flex h-[5rem] w-full items-center justify-center gap-[0.8rem] rounded-b-[10px] bg-green-500 text-white">
              <GavelIcon />
              <span className="w-[15rem] whitespace-nowrap text-[1.8rem] font-medium">
                Sold for {formatCurrency(product.price ?? 0)}!
              </span>
            </div>
          )}
      </Link>
      <div className="flex w-full flex-col justify-between gap-[1.6rem] py-[1.6rem]">
        <Link
          href={getProductUrl(product.slug)}
          prefetch={false}
          className="flex w-full flex-col gap-[1.6rem]"
        >
          <div className="flex w-full flex-col gap-[0.8rem]">
            <div className="flex w-full flex-col gap-[0.4rem]">
              <span className="flex line-clamp-3 text-[1.8rem] leading-[2.7rem] text-brand-lightest-black">
                {product.title}
              </span>
              <div className="isolate flex w-full items-center gap-[1.2rem]">
                {product.on_sale ? (
                  <div className="flex items-center gap-[0.8rem]">
                    <p className="text-[2.4rem] font-semibold leading-[2.4rem]">
                      {formatCurrency(product.price)}
                    </p>
                    <p className="text-[2.4rem] font-semibold leading-[2.4rem] text-gray-400 line-through">
                      {formatCurrency(product.og_price)}
                    </p>
                  </div>
                ) : (
                  <p className="text-[2.4rem] font-medium leading-[2.4rem]">
                    {formatCurrency(product.price)}
                  </p>
                )}
              </div>
              <span className="line-clamp-3 text-[1.3rem] leading-[2rem] text-brand-lightest-black">
                {product.description}
              </span>
            </div>
          </div>
        </Link>
        <div className="flex w-full justify-between gap-[2rem]">
          <div className="flex items-center gap-[2rem]">
            <span className="text-brand-lightest-black">
              Stock: {product.stock}
            </span>
            {/* <div className="flex gap-[0.4rem] items-center">
            <EyeOutlineIcon />
            <span className=" text-brand-lightest-black">
              {product.favorite_count}
            </span>
          </div> */}
            <div className="flex items-center gap-[0.4rem]">
              <HeartStraightIcon />
              <span className="text-brand-lightest-black">
                {product.favorite_count}
              </span>
            </div>
          </div>
          <div className="hidden h-[4rem] items-center justify-between gap-[1.6rem] px-[1.6rem] py-[0.8rem] font-semibold text-brand-secondary lg:flex">
            <EditDuplicateDeleteButtonsWithText product={product} />
          </div>
        </div>
      </div>
      <div className="flex h-[4rem] w-full items-center justify-between gap-[1.6rem] px-[1.6rem] py-[0.8rem] font-semibold text-brand-secondary lg:hidden">
        <EditDuplicateDeleteButtonsWithText product={product} />
      </div>
      <hr />
    </div>
  );
};

interface ProductListProps {
  view: 'grid' | 'list';
  products: ProductDocument[];
  onScroll: (ev: React.UIEvent<HTMLDivElement, UIEvent>) => void;
  selection?: SelectionApi;
}
const ProductList = ({
  view,
  products,
  onScroll,
  selection,
}: ProductListProps) => {
  return (
    <div className="flex w-full flex-col gap-[2rem] p-[1.6rem]">
      {view === 'grid' && (
        <div
          onScroll={onScroll}
          className="grid max-h-[80rem] w-full grid-cols-2 gap-[1.6rem] overflow-y-auto p-[1rem] lg:grid-cols-4"
        >
          {products.map((product) => (
            <ListingCardGrid
              key={product.id}
              product={product}
              selection={selection}
            />
          ))}
        </div>
      )}

      {view === 'list' && (
        <div
          onScroll={onScroll}
          className="flex max-h-[100rem] flex-col gap-[1.6rem]  overflow-y-scroll"
        >
          {products.map((product) => (
            <ListingCardList
              key={product.id}
              product={product}
              selection={selection}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** Selection API threaded into every card. */
export type SelectionApi = {
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
};

export type Availability = 'expired' | 'onSale' | 'inStock' | 'draft' | 'sold' | 'all';

interface Filters {
  condition: string;
  availability: Availability;
}
interface ListingFiltersProps {
  view: 'grid' | 'list';
  setView: (view: 'grid' | 'list') => void;
  filters: Filters;
  setFilters: (filters: Filters) => void;
}
const ListingFilters = ({
  view,
  setView,
  filters,
  setFilters,
}: ListingFiltersProps) => {
  const [conditions, setConditions] = useState<ConditionDocument[]>([]);
  
  useEffect(() => {
    const fetchConditions = async () => {
      const conditionsRes = await getConditions()
      setConditions(conditionsRes || [])
    }
  
    fetchConditions()
  }, [])

  const buttonToggles: ButtonToggleType[] = [
    {
      key: 'list',
      icon: <ListIcon />,
      onClick: () => {
        setView('list');
      },
      selected: view === 'list',
    },
    {
      key: 'grid',
      icon: <GridIcon />,
      onClick: () => {
        setView('grid');
      },
      selected: view === 'grid',
    },
  ];

  return (
    <div className="flex w-full flex-col justify-center gap-[1.6rem] p-[1.6rem] lg:flex-row lg:items-center">
      <h2 className="font-semibold text-brand-lightest-black">Filter</h2>
      <div className="flex w-full gap-[1.6rem]">
        <Select
          options={[{ label: 'All Conditions', value: 'all', id: 'all' }].concat(
            conditions?.map((condition) => ({
              label: condition.name,
              value: condition.name,
              id: condition.name,
            })) ?? []
          )}
          allowEmpty={false}
          placeholder="All Condition"
          value={filters.condition}
          onChange={(option) => {
            setFilters({
              ...filters,
              condition: option.id ?? 'all',
            });
          }}
        />
      </div>
      <div className="flex w-full gap-[1.6rem]">
        <Select
          options={[
            { label: 'All Availability', value: 'all', id: 'all' },
            { label: 'On Sale', value: 'onSale', id: 'onSale' },
            { label: 'Draft', value: 'draft', id: 'draft' },
            { label: 'In Stock', value: 'inStock', id: 'inStock' },
            { label: 'Sold', value: 'sold', id: 'sold' },
          ]}
          allowEmpty={false}
          placeholder="All Availability"
          value={filters.availability}
          onChange={(option) => {
            setFilters({
              ...filters,
              availability: option.id as Availability,
            });
          }}
        />
      </div>
      <div className="flex items-center justify-between gap-[1.6rem]">
        <ButtonToggle buttons={buttonToggles} />
      </div>
    </div>
  );
};

export const ListingsPage = () => {
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const [view, setView] = useState<'list' | 'grid'>('list');
  const router = useRouter();

  // --- Bulk selection ----------------------------------------------------
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<null | 'delete' | 'ebay'>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const selection: SelectionApi = useMemo(
    () => ({
      isSelected: (id) => selectedIds.has(id),
      toggle: (id) =>
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        }),
    }),
    [selectedIds]
  );
  function clearSelection() {
    setSelectedIds(new Set());
    setBulkError(null);
  }

  const [filters, setFilters] = useState<Filters>(() => ({
    condition: (searchParams.get('condition') as Condition) ?? 'all',
    availability: (searchParams.get('availability') as Availability) ?? 'all',
  }));
  const { userDoc } = useAuth();
  // Auth resolves in two steps (Firebase user, then user document). Firing
  // this query before it lands sent uid '' to Firestore, which returns a
  // perfectly successful empty page — indistinguishable from a seller with
  // no listings.
  const auth = useDashboardAuthState();
  const queryEnabled = auth.isReady;
  const {
    data,
    isLoading,
    isError,
    isFetching,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ['listings', userDoc?.uid, filters.condition, filters.availability],
    ({ pageParam }) =>
      getSellerListingsPaginated(
        userDoc?.uid ?? '',
        userDoc?.roles?.admin ?? false,
        pageParam,
        (filters.condition === 'all' ? null : filters.condition) ?? null,
        filters.availability !== 'all' ? filters.availability : null
      ),
    {
      enabled: queryEnabled,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => {
        // Must be undefined, not 0 or false: react-query only treats
        // undefined/null/false as "no more pages", so an empty page returning
        // the literal 0 (from `[].length &&`) kept hasNextPage true and let
        // every further scroll re-request page 1.
        const full =
          lastPage.results.length > 0 && lastPage.results.length % 12 === 0;
        return full
          ? lastPage.results[lastPage.results.length - 1].id
          : undefined;
      },
      onError: (err) =>
        reportDashboardError(
          'dashboard/listings: getSellerListingsPaginated failed',
          err
        ),
    }
  );
  const allProducts = data?.pages.flatMap((page) => page.results) ?? [];

  // `isLoading` is also true for a disabled query, so it only means "in
  // flight" once the query is enabled.
  // Listings already on screen win over every other state, so a transient
  // auth blip cannot replace them with a claim about the account. (This
  // query is keyed by uid, so a blip does empty it — but then the honest
  // state is "loading", which is what auth.isResolving gives us, and never
  // "you are signed out".)
  const hasProducts = allProducts.length > 0;
  const showLoading =
    !hasProducts && (auth.isResolving || (queryEnabled && isLoading));
  const showError = !hasProducts && !showLoading && auth.isReady && isError;
  // A failure while paging in more listings keeps the rows already on screen.
  const showPagingError = isError && allProducts.length > 0;
  const filtersApplied =
    filters.condition !== 'all' || filters.availability !== 'all';

  // --- Bulk actions ------------------------------------------------------
  const selectedProducts = allProducts.filter((p) => selectedIds.has(p.id));
  const deletableSelected = selectedProducts.filter(canDelete);
  const ebayEligibleSelected = selectedProducts.filter(canSendToEbay);
  const skippedSold = selectedProducts.length - deletableSelected.length;

  // Visible products that *can* be selected for delete (used by Select All).
  const visibleSelectable = allProducts.filter(canDelete);
  const allVisibleSelected =
    visibleSelectable.length > 0 &&
    visibleSelectable.every((p) => selectedIds.has(p.id));

  function selectAllVisible() {
    setSelectedIds(new Set(visibleSelectable.map((p) => p.id)));
  }

  async function bulkDelete() {
    if (deletableSelected.length === 0) return;
    const confirmed = window.confirm(
      `Delete ${deletableSelected.length} listing${
        deletableSelected.length === 1 ? '' : 's'
      }? This can’t be undone.${
        skippedSold > 0
          ? `\n\n${skippedSold} sold listing${
              skippedSold === 1 ? ' will be' : 's will be'
            } skipped.`
          : ''
      }`
    );
    if (!confirmed) return;

    setBulkBusy('delete');
    setBulkError(null);
    const results = await Promise.allSettled(
      deletableSelected.map((p) => deleteListing(p.id, p.is_draft))
    );
    setBulkBusy(null);

    // archived:true (sales history found) counts as success, not failure —
    // a 409-style refusal here would strand the listing in the drafts view
    // forever, the exact bug this PR fixes.
    const failures = results.filter(
      (r): r is PromiseRejectedResult => r.status === 'rejected'
    );
    const archivedCount = results.filter(
      (r) => r.status === 'fulfilled' && r.value.archived
    ).length;

    if (failures.length > 0) {
      // Distinct server-provided messages, deduped. Sales-history refusals
      // no longer land here (they archive instead), so what's left are
      // genuine transient failures — network/auth/permissions — where
      // "try again" is actually good advice.
      const messages = Array.from(
        new Set(
          failures.map((f) =>
            f.reason instanceof Error ? f.reason.message : 'Unknown error'
          )
        )
      );
      setBulkError(
        `${failures.length} of ${deletableSelected.length} delete${
          deletableSelected.length === 1 ? '' : 's'
        } failed: ${messages.join('; ')}. Please try again.`
      );
    } else if (archivedCount > 0) {
      setBulkError(
        `${archivedCount} listing${archivedCount === 1 ? '' : 's'} had sales history and ${
          archivedCount === 1 ? 'was' : 'were'
        } archived instead of deleted.`
      );
    }
    clearSelection();
    // Drop cached pages — they still contain the deleted IDs until refetch.
    await queryClient.invalidateQueries(['listings']);
  }

  const ebayEnabled = canUseProvider(
    userDoc?.permissions?.integrations,
    'ebay'
  );

  async function bulkSendToEbay() {
    if (ebayEligibleSelected.length === 0) return;
    setBulkBusy('ebay');
    setBulkError(null);
    try {
      const results = await publishToEbay(
        ebayEligibleSelected.map((p) => p.id)
      );
      const failed = results.filter((r) => r.status === 'error');
      const queued = results.length - failed.length;
      if (failed.length > 0) {
        setBulkError(
          `${queued} sent to eBay. ${failed.length} failed: ${failed
            .slice(0, 3)
            .map((f) => f.error)
            .join('; ')}${failed.length > 3 ? '…' : ''}`
        );
      } else {
        setBulkError(null);
        clearSelection();
      }
    } catch (err) {
      setBulkError(
        err instanceof Error ? err.message : 'Failed to send to eBay'
      );
    } finally {
      setBulkBusy(null);
    }
  }

  // Sync filter state → URL (shallow replace, no data re-fetch)
  useEffect(() => {
    const params: Record<string, string> = {};
    if (filters.condition !== 'all') params.condition = filters.condition;
    if (filters.availability !== 'all') params.availability = filters.availability;

    router.replace(
      { pathname: '/dashboard/listings', query: params },
      undefined,
      { shallow: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.condition, filters.availability]);


  function onScroll(ev: React.UIEvent<HTMLDivElement, UIEvent>) {
    // isError guard: a failed page leaves data.pages unchanged, so
    // hasNextPage stays true and every further scroll would re-fire the same
    // failing request (and its error report) indefinitely. The banner's retry
    // is the way back.
    if (isLoading || isFetchingNextPage || !hasNextPage || isError) return;
    const el = ev.target as HTMLDivElement;
    // if scroll is at top of the page, refresh the chats
    if (el.scrollTop === 0) {
      fetchNextPage();
      return;
    }

    const threshold = el.clientHeight * 0.5;
    const thresholdReached =
      el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (thresholdReached) fetchNextPage();
  }

  return (
    <div className="flex flex-col ">
      <div className="flex items-center justify-between p-[1.6rem]">
        <h1 className="text-[2.4rem] font-semibold">Listings</h1>
        <Button
          type="secondary"
          text="Create Listing"
          width="small"
          href="/sell/create-listing"
          leadingIcon={<PlusIcon />}
        />
      </div>
      <Divider />
      <ListingFilters
        view={view}
        setView={setView}
        filters={filters}
        setFilters={setFilters}
      />
      <Divider />
      {hasProducts ? (
        <>
          <BulkActionBar
            allVisibleSelected={allVisibleSelected}
            visibleSelectableCount={visibleSelectable.length}
            selectedCount={selectedIds.size}
            deletableCount={deletableSelected.length}
            ebayEligibleCount={ebayEligibleSelected.length}
            busy={bulkBusy}
            error={bulkError}
            onSelectAll={selectAllVisible}
            onClear={clearSelection}
            onDelete={bulkDelete}
            onSendToEbay={bulkSendToEbay}
            ebayEnabled={ebayEnabled}
            sendToEbayDisabledReason={
              !ebayEnabled
                ? 'Connect & enable eBay in Integrations first'
                : undefined
            }
          />
          <ProductList
            view={view}
            products={allProducts}
            onScroll={onScroll}
            selection={selection}
          />
          {showPagingError && (
            <div
              role="alert"
              className="m-[1.6rem] flex flex-wrap items-center justify-between gap-[1.2rem] rounded-[0.8rem] bg-red-50 px-[1.6rem] py-[1.2rem]"
            >
              <span className="text-brand-red">
                We couldn&apos;t load more listings.
              </span>
              <Button
                type="text"
                width="min"
                text={isFetching ? 'Retrying' : 'Try again'}
                onClick={() => fetchNextPage()}
                disabled={isFetching}
              />
            </div>
          )}
        </>
      ) : showLoading ? (
        // skeleton
        <div
          className="mt-4 flex h-full w-full grow flex-col items-center justify-center"
          aria-busy="true"
          aria-label="Loading listings"
        >
          <div className="flex w-full animate-pulse flex-col gap-[3.2rem]">
            <div className="flex w-full flex-col gap-2">
              <div className="h-4 w-full rounded bg-gray-300"></div>
              <div className="h-4 w-1/2 rounded bg-gray-300"></div>
            </div>
            <div className="flex w-full flex-col gap-2">
              <div className="h-4 w-full rounded bg-gray-300"></div>
              <div className="h-4 w-1/2 rounded bg-gray-300"></div>
            </div>
            <div className="flex w-full flex-col gap-2">
              <div className="h-4 w-full rounded bg-gray-300"></div>
              <div className="h-4 w-1/2 rounded bg-gray-300"></div>
            </div>
            <div className="flex w-full flex-col gap-2">
              <div className="h-4 w-full rounded bg-gray-300"></div>
              <div className="h-4 w-1/2 rounded bg-gray-300"></div>
            </div>
            <div className="flex w-full flex-col gap-2">
              <div className="h-4 w-full rounded bg-gray-300"></div>
              <div className="h-4 w-1/2 rounded bg-gray-300"></div>
            </div>
          </div>
        </div>
      ) : auth.isSignedOut ? (
        <SignedOutState subject="listings" />
      ) : auth.isStalled ? (
        <AuthStalledState subject="listings" onRetry={auth.retry} />
      ) : showError ? (
        <QueryErrorState
          subject="listings"
          onRetry={() => refetch()}
          isRetrying={isFetching}
        />
      ) : (
        <div className="mt-4 flex h-full w-full grow flex-col items-center justify-center">
          <EmptyState
            icon={undefined}
            title={filtersApplied ? 'No Listings Found' : 'No Listings Yet'}
            description={
              filtersApplied
                ? 'No listings match these filters.'
                : 'No listings yet!'
            }
          />
        </div>
      )}
    </div>
  );
};

/**
 * Sticky-feeling action row shown above the product list. Lets the seller
 * select-all-visible, clear, bulk-delete (sold are skipped), and bulk-send
 * to eBay (button present but disabled until the publish path is wired).
 */
function BulkActionBar({
  allVisibleSelected,
  visibleSelectableCount,
  selectedCount,
  deletableCount,
  ebayEligibleCount,
  busy,
  error,
  onSelectAll,
  onClear,
  onDelete,
  onSendToEbay,
  ebayEnabled,
  sendToEbayDisabledReason,
}: {
  allVisibleSelected: boolean;
  visibleSelectableCount: number;
  selectedCount: number;
  deletableCount: number;
  ebayEligibleCount: number;
  busy: null | 'delete' | 'ebay';
  error: string | null;
  onSelectAll: () => void;
  onClear: () => void;
  onDelete: () => void;
  onSendToEbay: () => void;
  ebayEnabled?: boolean;
  sendToEbayDisabledReason?: string;
}) {
  const ebayDisabled =
    !ebayEnabled || ebayEligibleCount === 0 || busy === 'ebay';
  const hasSelection = selectedCount > 0;

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-brand-lighter-gray bg-brand-white px-[1.6rem] py-[1.2rem]">
      {/* Select all — brand checkmark */}
      <CheckmarkIconButton
        checked={allVisibleSelected}
        disabled={visibleSelectableCount === 0}
        onClick={() => (allVisibleSelected ? onClear() : onSelectAll())}
        label={`Select all (${visibleSelectableCount})`}
        labelClassName="text-[1.4rem] text-brand-lightest-black"
      />

      {/* Selected count chip */}
      {hasSelection && (
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-brand-secondary/10 px-3 py-1 text-[1.3rem] font-medium text-brand-secondary">
            {selectedCount} selected
          </span>
          <button
            type="button"
            onClick={onClear}
            className="text-[1.3rem] text-brand-dark-gray hover:text-brand-secondary"
          >
            Clear
          </button>
        </div>
      )}

      {/* Actions — only meaningful once something is selected */}
      <div className="ml-auto flex items-center gap-[0.8rem]">
        <Button
          type="tertiary"
          height="extraSmall"
          text={
            busy === 'ebay'
              ? 'Sending…'
              : `Send to eBay${ebayEligibleCount ? ` (${ebayEligibleCount})` : ''}`
          }
          onClick={onSendToEbay}
          disabled={ebayDisabled}
          loading={busy === 'ebay'}
          title={sendToEbayDisabledReason}
        />
        <Button
          type="decline"
          height="extraSmall"
          text={busy === 'delete' ? 'Deleting…' : `Delete${deletableCount ? ` (${deletableCount})` : ''}`}
          onClick={onDelete}
          disabled={deletableCount === 0 || busy === 'delete'}
          loading={busy === 'delete'}
        />
      </div>

      {error && (
        <div className="basis-full rounded-[0.8rem] bg-red-50 px-3 py-2 text-[1.3rem] text-brand-red">
          {error}
        </div>
      )}
    </div>
  );
}
